'use client';

import { useState, useEffect } from 'react';
import { Sun, CloudSun, Cloud, CloudOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AuroraData } from '@/lib/aurora';
import AuroraStatusBanner from '@/components/aurora/AuroraStatusBanner';
import SolarWindPanel from '@/components/aurora/SolarWindPanel';
import {
  resolveAuroraLabels,
  type AuroraLabels,
} from '@/components/aurora/labels';

function getTrendArrow(trend: 'rising' | 'falling' | 'stable'): string {
  if (trend === 'rising') return '\u2191';
  if (trend === 'falling') return '\u2193';
  return '\u2192';
}

function getActivityColor(activity: 'quiet' | 'moderate' | 'high' | 'storm'): string {
  switch (activity) {
    case 'quiet':
      return '#22C55E';
    case 'moderate':
      return '#EAB308';
    case 'high':
      return '#F97316';
    case 'storm':
      return '#EF4444';
  }
}

function getCloudIcon(cloudCover: number | null) {
  if (cloudCover === null) return <CloudOff className="h-8 w-8 text-[#8A8A9A]" />;
  if (cloudCover < 25) return <Sun className="h-8 w-8 text-[#EAB308]" />;
  if (cloudCover <= 50) return <CloudSun className="h-8 w-8 text-[#8A8A9A]" />;
  if (cloudCover <= 75) return <Cloud className="h-8 w-8 text-[#8A8A9A]" />;
  return <CloudOff className="h-8 w-8 text-[#8A8A9A]" />;
}

function getKpColor(kp: number): string {
  if (kp >= 7) return '#EF4444';
  if (kp >= 5) return '#F97316';
  if (kp >= 3) return '#EAB308';
  return '#22C55E';
}

export default function AuroraDashboard({ labels }: { labels?: AuroraLabels }) {
  const L = resolveAuroraLabels(labels);
  const [data, setData] = useState<AuroraData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [cloudCover, setCloudCover] = useState<number | null>(null);

  useEffect(() => {
    Promise.all([
      fetch('/api/aurora')
        .then((r) => {
          if (!r.ok) throw new Error('aurora fetch failed');
          return r.json();
        }),
      fetch('/api/weather')
        .then((r) => {
          if (!r.ok) throw new Error('weather fetch failed');
          return r.json();
        })
        .catch(() => null),
    ])
      .then(([auroraData, weatherData]) => {
        setData(auroraData);
        if (weatherData?.levi?.cloudCover != null) {
          // Convert oktas (0-8) to percentage
          setCloudCover(Math.round((weatherData.levi.cloudCover / 8) * 100));
        }
      })
      .catch(() => setError(true))
      .finally(() => setLoading(false));
  }, []);

  if (error) {
    return (
      <div
        className={cn(
          'rounded-xl p-8 text-center text-sm',
          'bg-white text-[#5A5A6E] shadow-sm',
        )}
      >
        {L.unavailable}
      </div>
    );
  }

  const displayCloudCover = data?.cloudCover ?? cloudCover;

  return (
    <div className="space-y-6">
      {/* 1. Status Banner */}
      <AuroraStatusBanner data={data} loading={loading} labels={labels} />

      {/* 2. Real-time conditions - 3 cards */}
      <div className="grid gap-4 md:grid-cols-3">
        {/* Kp Index */}
        {loading ? (
          <div
            className={cn(
              'h-44 rounded-xl animate-pulse',
              'bg-white shadow-sm',
            )}
          />
        ) : data ? (
          <div
            className={cn(
              'rounded-xl p-6 transition-colors duration-300',
              'bg-white shadow-sm',
            )}
          >
            <p
              className={cn(
                'text-xs font-sans uppercase tracking-wider mb-4',
                'text-[#5A5A6E]',
              )}
            >
              {L.kpTitle}
            </p>
            <div className="flex items-center gap-4">
              <div
                className="flex items-center justify-center w-24 h-24 rounded-full"
                style={{ border: `3px solid ${getKpColor(data.kp.current)}` }}
              >
                <span
                  className="text-3xl font-sans font-semibold"
                  style={{ color: getKpColor(data.kp.current) }}
                >
                  {data.kp.current}
                </span>
              </div>
              <div>
                <span
                  className={cn(
                    'text-lg',
                    'text-[#1A1A2E]',
                  )}
                >
                  {getTrendArrow(data.kp.trend)} {L.trends[data.kp.trend]}
                </span>
              </div>
            </div>
            <p
              className={cn(
                'text-xs mt-3',
                'text-[#5A5A6E]',
              )}
            >
              {L.kpNote}
            </p>
          </div>
        ) : null}

        {/* Magnetometer Stations */}
        {loading ? (
          <div
            className={cn(
              'h-44 rounded-xl animate-pulse',
              'bg-white shadow-sm',
            )}
          />
        ) : data ? (
          <div
            className={cn(
              'rounded-xl p-6 transition-colors duration-300',
              'bg-white shadow-sm',
            )}
          >
            <p
              className={cn(
                'text-xs font-sans uppercase tracking-wider mb-4',
                'text-[#5A5A6E]',
              )}
            >
              {L.magnetometersTitle}
            </p>
            <div className="space-y-3">
              {(data.fmi.stations ?? []).map((station) => (
                <div key={station.code} className="flex items-center justify-between">
                  <div>
                    <span
                      className={cn(
                        'text-sm font-medium',
                        'text-[#1A1A2E]',
                      )}
                    >
                      {station.name}
                    </span>
                    <span
                      className={cn(
                        'text-xs ml-1.5',
                        'text-[#5A5A6E]',
                      )}
                    >
                      ({station.code})
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={cn(
                        'text-2xl font-sans font-semibold',
                        'text-[#1A1A2E]',
                      )}
                    >
                      {station.rIndex !== null ? station.rIndex : '\u2014'}
                    </span>
                    <span
                      className="inline-block rounded-full px-2 py-0.5 text-xs font-medium text-white"
                      style={{ backgroundColor: getActivityColor(station.activity) }}
                    >
                      {L.activity[station.activity]}
                    </span>
                  </div>
                </div>
              ))}
            </div>
            <p
              className={cn(
                'text-xs mt-3',
                'text-[#5A5A6E]',
              )}
            >
              {L.magnetometersNote}
            </p>
          </div>
        ) : null}

        {/* Cloud Cover */}
        {loading ? (
          <div
            className={cn(
              'h-44 rounded-xl animate-pulse',
              'bg-white shadow-sm',
            )}
          />
        ) : (
          <div
            className={cn(
              'rounded-xl p-6 transition-colors duration-300',
              'bg-white shadow-sm',
            )}
          >
            <p
              className={cn(
                'text-xs font-sans uppercase tracking-wider mb-4',
                'text-[#5A5A6E]',
              )}
            >
              {L.cloudCoverTitle}
            </p>
            <div className="flex items-center gap-4">
              {getCloudIcon(displayCloudCover)}
              <span
                className={cn(
                  'text-5xl font-sans font-semibold',
                  'text-[#1A1A2E]',
                )}
              >
                {displayCloudCover !== null ? `${displayCloudCover}%` : '\u2014'}
              </span>
            </div>
            <p
              className={cn(
                'text-xs mt-3',
                'text-[#5A5A6E]',
              )}
            >
              {L.cloudNote}
            </p>
          </div>
        )}
      </div>

      {/* 3. Kp Forecast Chart - rendered only if component exists */}
      {data?.kp.forecast && data.kp.forecast.length > 0 && (
        <KpForecastChartLazy forecast={data.kp.forecast} labels={labels} />
      )}

      {/* 4. Solar Wind Panel */}
      <SolarWindPanel labels={labels} />
    </div>
  );
}

/**
 * Lazy wrapper for KpForecastChart - renders nothing if the module is unavailable.
 */
function KpForecastChartLazy({
  forecast,
  labels,
}: {
  forecast: AuroraData['kp']['forecast'];
  labels?: AuroraLabels;
}) {
  const [Component, setComponent] = useState<React.ComponentType<{ forecast: AuroraData['kp']['forecast']; labels?: AuroraLabels }> | null>(null);

  useEffect(() => {
    import('@/components/aurora/KpForecastChart')
      .then((mod) => setComponent(() => mod.default))
      .catch(() => {
        // KpForecastChart not yet created - silently skip
      });
  }, []);

  if (!Component) return null;
  return <Component forecast={forecast} labels={labels} />;
}
